import { useEffect, useState } from "react";
import { Calculator, BookOpen, Warning, SealCheck, CaretDown, CaretRight, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CCNav, PeriodBar, usePeriods, fmtAmt } from "../components/cc/common";
import { CCGuide } from "../components/cc/GuideBanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ENTRY_LABEL = { base: "base", bonus: "bonus", chargeback: "chargeback", bonus_reversal: "bonus reversal", adjustment: "adjustment" };
const ENTRY_STYLE = {
  base: "bg-emerald-100 text-emerald-800", bonus: "bg-indigo-100 text-indigo-800",
  chargeback: "bg-red-100 text-red-700", bonus_reversal: "bg-orange-100 text-orange-800",
  adjustment: "bg-sky-100 text-sky-800",
};

const CLASS_OPTS = [
  { v: "new_business", l: "New business" }, { v: "renewal", l: "Renewal" },
  { v: "endorsement", l: "Endorsement" }, { v: "reinstatement", l: "Reinstatement" },
];

function ReclassControl({ ln, onSaved }) {
  const [cls, setCls] = useState(ln.txn_class || "");
  const [saving, setSaving] = useState(false);
  const overridden = !!(ln.explanation || {}).reclassified;
  const save = async (clear) => {
    setSaving(true);
    try {
      await api.put("/commission/calc/reclassify", { row_id: ln.row_id, txn_class: clear ? null : cls });
      toast.success(clear ? "Override cleared — recompute to apply" : "Class saved — recompute to apply");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid={`cc-reclass-${ln.id}`}>
      <span className="text-xs text-slate-500">Transaction class{overridden ? " (manually set)" : ""}:</span>
      <Select value={cls} onValueChange={setCls}>
        <SelectTrigger className="h-7 w-40 text-xs" data-testid={`cc-reclass-select-${ln.id}`}><SelectValue placeholder="Pick a class" /></SelectTrigger>
        <SelectContent>{CLASS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" variant="outline" className="h-7 text-xs" disabled={saving || !cls || cls === ln.txn_class}
        onClick={() => save(false)} data-testid={`cc-reclass-save-${ln.id}`}>Set class</Button>
      {overridden && (
        <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" disabled={saving}
          onClick={() => save(true)} data-testid={`cc-reclass-clear-${ln.id}`}>Clear override</Button>
      )}
    </div>
  );
}

function LabelDecisionControl({ ln, onSaved }) {
  const [cands, setCands] = useState(null);
  const [pick, setPick] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/commission/policies/${ln.policy_id}/label-candidates`).then((r) => {
      setCands(r.data.candidates);
      if (r.data.decision) setPick(r.data.decision.producer_id);
    }).catch(() => setCands([]));
  }, [ln.policy_id]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/commission/policies/${ln.policy_id}/label-decision`, { producer_id: pick });
      toast.success("Credit decision saved — recompute to apply");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  if (!cands || !cands.length) return null;
  return (
    <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid={`cc-labelpick-${ln.id}`}>
      <span className="text-xs text-slate-500">Who gets the credit:</span>
      <Select value={pick} onValueChange={setPick}>
        <SelectTrigger className="h-7 w-52 text-xs" data-testid={`cc-labelpick-select-${ln.id}`}><SelectValue placeholder="Pick a team member" /></SelectTrigger>
        <SelectContent>{cands.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.status === "former" ? " (former)" : ""}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!pick || saving}
        onClick={save} data-testid={`cc-labelpick-save-${ln.id}`}>Give credit</Button>
    </div>
  );
}

function KPI({ label, value, sub, testId, alert }) {
  return (
    <div className={`rounded-lg border p-4 ${alert ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`} data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-xl font-semibold mt-1 ${alert ? "text-red-700" : ""}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function LineDetail({ ln }) {
  const ex = ln.explanation || {};
  return (
    <div className="text-xs bg-slate-50 rounded p-2.5 space-y-0.5">
      {ln.exception_detail && <p className="text-red-700 font-medium">{ln.exception_type}: {ln.exception_detail}</p>}
      {ln.waiver_note && <p className="text-amber-700">Waived (approved non-payable): {ln.waiver_note}</p>}
      {Object.entries(ex).map(([k, v]) => <p key={k}><span className="text-slate-400">{k}:</span> {String(v)}</p>)}
    </div>
  );
}

export default function CommissionLedgerPage() {
  const { hasPerm } = useAuth();
  const { periods, selected, setSelected, reload } = usePeriods();
  const [run, setRun] = useState(null);
  const [lines, setLines] = useState([]);
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [waiveLine, setWaiveLine] = useState(null);
  const [waiveNote, setWaiveNote] = useState("");
  const [sel, setSel] = useState({});
  const [chargeOpen, setChargeOpen] = useState(false);
  const [chargeNote, setChargeNote] = useState("");
  const [expanded, setExpanded] = useState({});
  const [expandedUser, setExpandedUser] = useState({});
  const [adjOpen, setAdjOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [adj, setAdj] = useState({ user_id: "", amount: "", reason: "" });

  const canResolve = hasPerm("commission.resolve");
  const canAdjust = hasPerm("commission.adjust");

  const load = async (pk = selected) => {
    if (!pk) return;
    const [runs, sum, ents] = await Promise.all([
      api.get(`/commission/calc/runs?period_key=${pk}`),
      api.get(`/commission/ledger/summary?period_key=${pk}`),
      api.get(`/commission/ledger?period_key=${pk}`),
    ]);
    const latest = runs.data.find((r) => r.status !== "superseded") || runs.data[0] || null;
    setRun(latest);
    setSummary(sum.data);
    setEntries(ents.data);
    if (latest) {
      const { data } = await api.get(`/commission/calc/${latest.id}/lines`);
      setLines(data);
    } else setLines([]);
  };
  useEffect(() => { load(); }, [selected]);
  useEffect(() => {
    if (canAdjust) api.get("/users").then((r) => setMembers(r.data.filter((u) => u.is_active !== false))).catch(() => {});
  }, [canAdjust]);

  const compute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/commission/calc/run", { period_key: selected });
      toast.success(`Computed v${data.version}: net payable ${data.totals.net_payable}, ${data.totals.blocking} blocking exception(s)`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Compute failed"); }
    finally { setBusy(false); }
  };

  const post = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/commission/calc/${run.id}/post`);
      toast.success(`Posted to ledger: ${data.posted} new entries (${data.skipped} already posted)`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Post failed"); }
    finally { setBusy(false); }
  };

  const waive = async () => {
    try {
      await api.put(`/commission/calc/lines/${waiveLine.id}/waive`, { note: waiveNote });
      toast.success("Approved as non-payable — recompute before posting (inputs changed)");
      setWaiveLine(null); setWaiveNote("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Waive failed"); }
  };

  const postAdj = async () => {
    setBusy(true);
    try {
      await api.post("/commission/ledger/adjustment", { ...adj, period_key: selected });
      toast.success("Adjustment posted (append-only ledger entry)");
      setAdjOpen(false); setAdj({ user_id: "", amount: "", reason: "" });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Adjustment failed"); }
    finally { setBusy(false); }
  };

  const t = run?.totals || {};
  const exceptions = lines.filter((l) => l.status === "exception");
  const waived = lines.filter((l) => l.status === "waived");
  const house = lines.filter((l) => l.status === "house_retained");
  const payable = lines.filter((l) => l.status === "payable" || l.status === "already_posted");
  const chargeable = exceptions.filter((l) => l.exception_type === "chargeback_unlinked");
  const selIds = Object.keys(sel).filter((k) => sel[k]);

  const chargeToProducer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/commission/calc/charge-to-producer", { line_ids: selIds, note: chargeNote });
      toast.success(`${data.stored} chargeback(s) directed to the policy's producer — recompute to apply`);
      setChargeOpen(false); setChargeNote(""); setSel({});
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const byUser = {};
  payable.forEach((l) => {
    const k = l.user_id || "none";
    byUser[k] = byUser[k] || { name: l.user_name || "—", lines: [], net: 0, producers: [] };
    byUser[k].lines.push(l);
    if (l.producer_name && !byUser[k].producers.includes(l.producer_name)) byUser[k].producers.push(l.producer_name);
    byUser[k].net += Number(l.payout || 0);
  });
  const blocking = Number(t.blocking || 0);

  return (
    <div data-testid="commission-ledger-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-heading font-semibold flex items-center gap-2">
            <BookOpen size={26} weight="duotone" className="text-indigo-600" /> Calculation & ledger
          </h1>
          <p className="text-sm text-slate-500 mt-1">Deterministic engine → every payable dollar traces to source, rule version and decision. Ledger is append-only.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodBar periods={periods} selected={selected} setSelected={setSelected}
            onCreated={async (k) => { await reload(); setSelected(k); }} />
          {canResolve && (
            <Button size="sm" disabled={busy || !selected} onClick={compute} data-testid="cc-compute-btn">
              <Calculator size={15} className="mr-1" /> Compute
            </Button>
          )}
        </div>
      </div>
      <CCNav />
      <CCGuide periodKey={selected} />

      {!run ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-500">
          No calculation run for {selected || "this period"} yet — hit Compute to evaluate every matched transaction against your rules.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3 text-xs text-slate-500 flex-wrap" data-testid="cc-run-meta">
            <Badge className={run.status === "posted" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"}>run v{run.version} · {run.status}</Badge>
            <span>engine {run.engine_version} · computed by {run.computed_by_name} {fmtDate(run.computed_at)}</span>
            <span className="font-mono">manifest {String(run.manifest_hash).slice(0, 10)}…</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
            <KPI label="Producer base" value={fmtAmt(t.payable_base)} testId="cc-kpi-base" />
            <KPI label="Bonuses" value={fmtAmt(t.bonuses)} testId="cc-kpi-bonuses" />
            <KPI label="Chargebacks" value={fmtAmt(t.chargebacks)} testId="cc-kpi-chargebacks" />
            <KPI label="Net payable (this run)" value={fmtAmt(t.net_payable)} sub={`${t.payable || 0} payable · ${t.already_posted || 0} already posted`} testId="cc-kpi-net" />
            <KPI label="Blocking exceptions" value={blocking} alert={blocking > 0}
              sub={`${t.waived || 0} waived · ${fmtAmtText(t.exception_carrier_amount)} carrier $ excepted`} testId="cc-kpi-blocking" />
          </div>

          {(exceptions.length > 0 || waived.length > 0) && (
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden mb-5">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
                <Warning size={16} className="text-amber-600" />
                <h2 className="font-medium text-sm">Exceptions — every transaction needs a final disposition</h2>
                {chargeable.length > 0 && canResolve && (
                  <div className="ml-auto flex items-center gap-2">
                    <button className="text-xs text-indigo-600 hover:underline" data-testid="cc-charge-select-all"
                      onClick={() => {
                        const all = chargeable.every((l) => sel[l.id]);
                        const next = {};
                        if (!all) chargeable.forEach((l) => { next[l.id] = true; });
                        setSel(next);
                      }}>
                      {chargeable.every((l) => sel[l.id]) ? "Clear selection" : `Select all ${chargeable.length} unlinked chargebacks`}
                    </button>
                    <Button size="sm" className="h-7 text-xs" disabled={selIds.length === 0}
                      onClick={() => setChargeOpen(true)} data-testid="cc-charge-bulk-btn">
                      Charge to producer ({selIds.length})
                    </Button>
                  </div>
                )}
              </div>
              <div className="divide-y divide-slate-50" data-testid="cc-exceptions-list">
                {[...exceptions, ...waived].map((l) => (
                  <div key={l.id} className="px-4 py-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex items-start gap-2">
                        {l.exception_type === "chargeback_unlinked" && l.status === "exception" && canResolve && (
                          <input type="checkbox" className="mt-1 accent-indigo-600" checked={!!sel[l.id]}
                            onChange={(e) => setSel({ ...sel, [l.id]: e.target.checked })}
                            data-testid={`cc-charge-select-${l.id}`} />
                        )}
                        <div className="min-w-0 cursor-pointer" onClick={() => setExpanded({ ...expanded, [l.id]: !expanded[l.id] })}>
                          <div className="text-sm flex items-center gap-2 flex-wrap">
                            {expanded[l.id] ? <CaretDown size={13} /> : <CaretRight size={13} />}
                            <span className="font-mono text-xs">{l.policy_number || "—"}</span>
                            <span className="text-slate-500">{l.insured_name || ""}</span>
                            {fmtAmt(l.carrier_amount)}
                            <Badge className={l.status === "waived" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"}>
                              {l.status === "waived" ? "waived" : l.exception_type}
                            </Badge>
                          </div>
                          {!expanded[l.id] && <p className="text-xs text-slate-400 truncate">{l.exception_detail}</p>}
                        </div>
                      </div>
                      {l.status === "exception" && canResolve && (
                        <div className="flex gap-1.5 shrink-0">
                          {l.exception_type === "chargeback_unlinked" && (
                            <Button size="sm" variant="outline" className="h-7 text-xs text-indigo-700"
                              onClick={() => { setSel({ [l.id]: true }); setChargeOpen(true); }}
                              data-testid={`cc-charge-one-${l.id}`}>Charge producer</Button>
                          )}
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setWaiveLine(l)} data-testid={`cc-waive-${l.id}`}>Waive</Button>
                        </div>
                      )}
                    </div>
                    {expanded[l.id] && (
                      <div className="mt-2">
                        <LineDetail ln={l} />
                        {canResolve && l.row_id && Number(l.carrier_amount) >= 0 && !["label_conflict", "assignment_hold"].includes(l.exception_type) && <ReclassControl ln={l} onSaved={load} />}
                        {canResolve && l.exception_type === "label_conflict" && l.policy_id && <LabelDecisionControl ln={l} onSaved={load} />}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {house.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden mb-5" data-testid="cc-house-list">
              <div className="px-4 py-3 border-b border-slate-100">
                <h2 className="font-medium text-sm">Retained by agency — former producers ({house.length})</h2>
                <p className="text-xs text-slate-400 mt-0.5">The producer stays on the policy for reporting, but these amounts are kept/absorbed by the agency and never paid out.</p>
              </div>
              <div className="divide-y divide-slate-50">
                {house.map((l) => (
                  <div key={l.id} className="px-4 py-2.5">
                    <div className="text-sm flex items-center gap-2 flex-wrap cursor-pointer"
                      onClick={() => setExpanded({ ...expanded, [l.id]: !expanded[l.id] })}>
                      {expanded[l.id] ? <CaretDown size={13} /> : <CaretRight size={13} />}
                      <span className="font-mono text-xs">{l.policy_number || "—"}</span>
                      <span className="text-slate-500">{l.insured_name || ""}</span>
                      {fmtAmt(l.carrier_amount)}
                      <Badge className="bg-slate-200 text-slate-600">house · {l.producer_name}</Badge>
                    </div>
                    {expanded[l.id] && <div className="mt-2"><LineDetail ln={l} /></div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden mb-5">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-medium text-sm">Producer allocations (this run)</h2>
              {canAdjust && run.status !== "posted" && (
                <Button size="sm" disabled={busy || blocking > 0} onClick={post} data-testid="cc-post-btn"
                  title={blocking > 0 ? "Resolve or waive blocking exceptions first" : ""}>
                  <SealCheck size={15} className="mr-1" /> Post to ledger
                </Button>
              )}
            </div>
            {Object.keys(byUser).length === 0 ? (
              <p className="p-6 text-sm text-slate-500">No payable allocations in this run.</p>
            ) : (
              <div className="divide-y divide-slate-50" data-testid="cc-allocations">
                {Object.entries(byUser).map(([uid, u]) => (
                  <div key={uid}>
                    <button className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-slate-50"
                      onClick={() => setExpandedUser({ ...expandedUser, [uid]: !expandedUser[uid] })} data-testid={`cc-alloc-user-${uid}`}>
                      <span className="text-sm font-medium flex items-center gap-1.5">
                        {expandedUser[uid] ? <CaretDown size={13} /> : <CaretRight size={13} />} {u.name}
                        <span className="text-xs text-slate-400">({u.lines.length} line{u.lines.length > 1 ? "s" : ""}{u.producers.length > 0 ? ` · ${u.producers.join(", ")}` : ""})</span>
                      </span>
                      <span className="text-sm font-semibold">{fmtAmt(u.net.toFixed(2))}</span>
                    </button>
                    {expandedUser[uid] && (
                      <div className="px-4 pb-3 space-y-1.5">
                        {u.lines.map((l) => (
                          <div key={l.id} className="border border-slate-100 rounded p-2">
                            <div className="flex items-center justify-between text-sm flex-wrap gap-1">
                              <span className="flex items-center gap-2 flex-wrap">
                                <Badge className={ENTRY_STYLE[l.entry_type]}>{ENTRY_LABEL[l.entry_type]}</Badge>
                                <span className="font-mono text-xs">{l.policy_number}</span>
                                <span className="text-xs text-slate-500">{l.transaction_type_raw} · {l.transaction_date || "—"}</span>
                                {l.status === "already_posted" && <Badge className="bg-slate-100 text-slate-500">already posted</Badge>}
                              </span>
                              <span className="font-semibold">{fmtAmt(l.payout)}</span>
                            </div>
                            <LineDetail ln={l} />
                            {canResolve && l.row_id && l.entry_type === "base" && Number(l.carrier_amount) >= 0 && <ReclassControl ln={l} onSaved={load} />}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-medium text-sm">Posted ledger — {selected} (append-only, authoritative)</h2>
          {canAdjust && <Button size="sm" variant="outline" onClick={() => setAdjOpen(true)} data-testid="cc-adjustment-open"><Plus size={14} className="mr-1" /> Manual adjustment</Button>}
        </div>
        {summary && summary.producers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cc-ledger-summary-table">
              <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                <th className="px-4 py-2">Producer</th><th className="px-4 py-2 text-right">Base</th>
                <th className="px-4 py-2 text-right">Bonuses</th><th className="px-4 py-2 text-right">Chargebacks</th>
                <th className="px-4 py-2 text-right">Adjustments</th><th className="px-4 py-2 text-right">Net</th>
                <th className="px-4 py-2 text-right">Entries</th>
              </tr></thead>
              <tbody>
                {summary.producers.map((u) => (
                  <tr key={u.user_id || u.user_name} className="border-b border-slate-50">
                    <td className="px-4 py-2.5 font-medium">{u.user_name}</td>
                    <td className="px-4 py-2.5 text-right">{fmtAmt(u.base)}</td>
                    <td className="px-4 py-2.5 text-right">{fmtAmt(u.bonus)}</td>
                    <td className="px-4 py-2.5 text-right">{fmtAmt((Number(u.chargeback) + Number(u.bonus_reversal)).toFixed(2))}</td>
                    <td className="px-4 py-2.5 text-right">{fmtAmt(u.adjustment)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold" data-testid={`cc-ledger-net-${u.user_id}`}>{fmtAmt(u.net)}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-slate-400">{u.entries}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-semibold">
                  <td className="px-4 py-2.5">Total</td>
                  <td className="px-4 py-2.5 text-right">{fmtAmt(summary.totals.base)}</td>
                  <td className="px-4 py-2.5 text-right">{fmtAmt(summary.totals.bonus)}</td>
                  <td className="px-4 py-2.5 text-right">{fmtAmt((Number(summary.totals.chargeback) + Number(summary.totals.bonus_reversal)).toFixed(2))}</td>
                  <td className="px-4 py-2.5 text-right">{fmtAmt(summary.totals.adjustment)}</td>
                  <td className="px-4 py-2.5 text-right" data-testid="cc-ledger-net-total">{fmtAmt(summary.totals.net)}</td>
                  <td className="px-4 py-2.5 text-right text-xs">{entries.length}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <p className="p-6 text-sm text-slate-500">Nothing posted to the ledger for this period yet.</p>
        )}
      </div>

      <Dialog open={!!waiveLine} onOpenChange={(o) => !o && setWaiveLine(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Approve as non-payable (waive)</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">{waiveLine?.exception_type}: {waiveLine?.exception_detail}</p>
          <Textarea placeholder="Documented reason (min 10 chars) — this is the transaction's final disposition" value={waiveNote}
            onChange={(e) => setWaiveNote(e.target.value)} data-testid="cc-waive-note-input" />
          <Button disabled={waiveNote.trim().length < 10} onClick={waive} data-testid="cc-waive-confirm">Waive (audited)</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={chargeOpen} onOpenChange={(o) => !o && setChargeOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Charge {selIds.length} chargeback{selIds.length > 1 ? "s" : ""} to producer</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">
            Each selected row has no pre-system original to reverse. On the next <strong>Compute</strong>, it will be
            charged to its policy's <strong>current producer at their current rule rate</strong> (former producers'
            chargebacks are absorbed by the agency). Fully audited.
          </p>
          <Textarea placeholder="Documented reason (min 10 chars) — e.g. pre-system commissions, clawed back per agency policy"
            value={chargeNote} onChange={(e) => setChargeNote(e.target.value)} data-testid="cc-charge-note-input" />
          <Button disabled={busy || chargeNote.trim().length < 10} onClick={chargeToProducer} data-testid="cc-charge-confirm">
            Charge to producer (recompute after)
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Manual ledger adjustment — {selected}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Posted ledger entries are never edited — corrections are new signed entries with a documented reason.</p>
          <Select value={adj.user_id} onValueChange={(v) => setAdj({ ...adj, user_id: v })}>
            <SelectTrigger data-testid="cc-adj-user-select"><SelectValue placeholder="Team member" /></SelectTrigger>
            <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Signed amount, e.g. -50.00 or 120.00" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} data-testid="cc-adj-amount-input" />
          <Textarea placeholder="Reason + evidence (min 10 chars)" value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} data-testid="cc-adj-reason-input" />
          <Button disabled={busy || !adj.user_id || !adj.amount || adj.reason.trim().length < 10} onClick={postAdj} data-testid="cc-adj-save">Post adjustment</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function fmtAmtText(s) {
  if (s === null || s === undefined) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(s));
}
